import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import { toast } from "sonner";

interface AddOrderTypeDialogProps {
  onAddType: (typeName: string) => Promise<void>;
  trigger?: React.ReactNode;
  existingTypes?: string[];
}

export function AddOrderTypeDialog({ 
  onAddType, 
  trigger,
  existingTypes = []
}: AddOrderTypeDialogProps) {
  const [open, setOpen] = useState(false);
  const [newType, setNewType] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleAddType = async () => {
    if (!newType.trim()) {
      toast.error("Digite o nome do tipo de pedido");
      return;
    }

    if (existingTypes.includes(newType.trim())) {
      toast.error("Este tipo de pedido já existe");
      return;
    }

    try {
      setIsLoading(true);
      await onAddType(newType.trim());
      toast.success(`Tipo "${newType.trim()}" adicionado com sucesso!`);
      setNewType("");
      setOpen(false);
    } catch (error) {
      console.error("Erro ao adicionar tipo de pedido:", error);
      toast.error("Erro ao adicionar tipo de pedido");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button 
            variant="outline" 
            size="sm" 
            className="h-10 w-10 p-0"
            title="Adicionar novo tipo de pedido"
          >
            <Plus className="h-4 w-4" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adicionar Novo Tipo de Pedido</DialogTitle>
          <DialogDescription>
            Crie um novo tipo de pedido para seus trabalhos
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="type-name">Nome do Tipo</Label>
            <Input
              id="type-name"
              placeholder="Ex: Sublimação, Silkscreen, etc."
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleAddType();
                }
              }}
              disabled={isLoading}
              autoFocus
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleAddType}
            disabled={isLoading}
          >
            {isLoading ? "Adicionando..." : "Adicionar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
